import { CalendarDate } from "@internationalized/date";
import {
  ceilToStartOfDay,
  endOfContainingDay,
  type DateRange,
} from "@repo/app-types/entities/dateRange";
import { DateTime, Duration } from "luxon";
import { useCallback, useMemo, type FC } from "react";
import { useNow } from "../timestamps/timestampContext.js";
import {
  DateRangePickerLayout,
  type CalendarDateRange,
  type DateRangePreset,
} from "./dateRangePickerLayout.js";

export interface DateRangeDialogProps {
  value: DateRange;
  onChange: (newValue: DateRange) => void;
}

const PRESETS: readonly DateRangePreset<DateRange>[] = [7, 14, 30, 90].map(
  (days) => ({
    key: `last_${days}_days`,
    textValue: `${days}d`,
    label: `Last ${days} days`,
    value: {
      length: Duration.fromObject({ days }),
      end: undefined,
    },
    checkIsSelected: (value: DateRange) => {
      return (
        value.end === undefined && Math.round(value.length.as("days")) === days
      );
    },
  }),
);

export const DateRangeDialog: FC<DateRangeDialogProps> = (props) => {
  const now = useNow();

  const maxCalendarValue = useMemo(
    () => createCalendarDateFromDateTime(now.endOf("day"), "endOfDay"),
    [now],
  );

  const convertToCalendarValue = useCallback(
    (value: DateRange): CalendarDateRange => {
      let startDateTime, endDateTime;

      if (value.end) {
        startDateTime = value.end.minus(value.length);
        endDateTime = value.end;
      } else {
        startDateTime = now.minus(value.length);
        endDateTime = now;
      }

      return {
        start: createCalendarDateFromDateTime(startDateTime, "startOfDay"),
        end: createCalendarDateFromDateTime(endDateTime, "endOfDay"),
      };
    },
    [now],
  );

  return (
    <DateRangePickerLayout
      value={props.value}
      onChange={props.onChange}
      presets={PRESETS}
      maxCalendarValue={maxCalendarValue}
      selectionAlignment="end"
      convertToCalendarValue={convertToCalendarValue}
      convertFromCalendarValue={convertFromCalendarValue}
    />
  );
};

function convertFromCalendarValue(
  calendarValue: CalendarDateRange,
): DateRange | undefined {
  // Convert to the start of the day in the local time zone
  const startDateTime = createDateTimeFromCalendarDate(
    calendarValue.start,
    "startOfDay",
  );
  // Convert to the end of the day in the local time zone
  const endDateTime = createDateTimeFromCalendarDate(
    calendarValue.end,
    "endOfDay",
  );
  // Calculate the period length. Round to whole days to remove any artifacts from DST changes, which might otherwise cause unnatural behavior when we use it for later computations.
  const length = endDateTime
    .diff(startDateTime, "days")
    .mapUnits((x) => Math.round(x));

  if (!length.isValid) {
    return undefined;
  }

  return { length, end: endDateTime };
}

// Mirrors the query-side snapping (both bounds ceil to day boundaries) so
// the calendar highlights the same days the range actually covers.
function createCalendarDateFromDateTime(
  dateTime: DateTime<true>,
  affinity: "startOfDay" | "endOfDay",
): CalendarDate {
  const snapped =
    affinity === "startOfDay"
      ? ceilToStartOfDay(dateTime)
      : endOfContainingDay(dateTime);

  return new CalendarDate(snapped.year, snapped.month, snapped.day);
}

function createDateTimeFromCalendarDate(
  date: CalendarDate,
  affinity: "startOfDay" | "endOfDay",
): DateTime {
  const baseDateTime = DateTime.fromObject({
    year: date.year,
    month: date.month,
    day: date.day,
  });

  return affinity === "startOfDay"
    ? baseDateTime.startOf("day")
    : baseDateTime.endOf("day");
}
